import { useState, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import Captcha from "../components/Captcha";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { user } = useAuth();
  const nav = useNavigate();
  const captchaRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "customer" ? "/my-tickets" : "/workspace"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const captcha_token = await captchaRef.current?.getValue();
      const { data } = await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        captcha_token: captcha_token || "",
      });
      localStorage.setItem("beacon_token", data.token);
      toast.success("Welcome to Pixel Support");
      window.location.href = "/my-tickets";
    } catch (err) {
      setError(formatApiError(err));
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-14">
      <div className="beacon-card p-7 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl bg-blue-600 text-white grid place-items-center">
            <UserPlus className="w-5 h-5" />
          </span>
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Create your account</div>
            <div className="text-xs text-slate-500">Free — takes 30 seconds. Then you can submit and track tickets.</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="beacon-label">Full name</label>
            <input required className="beacon-input" data-testid="register-name-input"
              value={name} onChange={(e) => setName(e.target.value)} placeholder="Priya Sharma" />
          </div>
          <div>
            <label className="beacon-label">Email</label>
            <input required type="email" className="beacon-input" data-testid="register-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <div>
            <label className="beacon-label">Phone number <span className="text-slate-400 font-normal">(optional)</span></label>
            <input type="tel" className="beacon-input" data-testid="register-phone-input"
              value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" />
          </div>
          <div>
            <label className="beacon-label">Password</label>
            <input required type="password" minLength={6} className="beacon-input" data-testid="register-password-input"
              value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
          </div>
          <Captcha ref={captchaRef} action="register" testid="register-captcha" />
          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="register-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting} data-testid="register-submit-btn"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg">
            {submitting ? "Creating..." : "Create account"}
          </Button>
        </form>

        <div className="mt-5 text-xs text-slate-500 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-700 hover:text-blue-800 font-semibold" data-testid="go-login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
